import React, { useState, useMemo, useCallback } from 'react'
import { View, SectionList, ActivityIndicator, TouchableOpacity } from 'react-native'
import { useFocusEffect, useNavigation, useTheme } from '@react-navigation/native'
import { Screen, Text, Header, Icon } from '@components'
import { NotificationItem } from './components/NotificationItem'
import { NotificationTabs, type NotificationTabKey } from './components/NotificationTabs'
import { NotificationSkeleton } from './components/NotificationSkeleton'
import { NotificationsEmptyState } from './components/NotificationsEmptyState'
import { NotificationsLoadError } from './components/NotificationsLoadError'
import { PendingRequestsFooter, receivedWithoutCard } from './components/PendingRequestsFooter'
import getStyle from './style'
import dayjs from 'dayjs'
import {
  usePaginateNotifications,
  usePendingContactRequests,
  NotificationType,
  ContactRequestAnswer,
  type Notification as ApiNotification,
} from '../../hooks/api/queries'
import {
  useAnswerRequestedContacts,
  useKeepContactDone,
  useMarkNotificationAsRead,
  usePostponeKeepContact,
} from '../../hooks/api/mutations'
import { useMarkAllNotificationsAsRead } from '../../hooks/api/mutations/useMarkAllNotificationsAsRead'
import { useCreationStackIntegration, useScreen } from '@hooks'
import { useLanguage } from '@context'
import { birthDayOf, Scope, showApiErrorToast } from '@utils'
import { groupByDueSection, type DueSection } from '../../utils/notificationDue'
import { NotificationConfirmation, type Confirmation } from './components/NotificationConfirmation'

// Type for formatted notifications with additional properties
type FormattedNotification = ApiNotification & {
  message: string
  contactId?: string
  contactIds?: string[]
  contactRequestId?: string
  accepted?: boolean
  isRead: boolean
  description?: string
}

const SECTION_LABEL_KEYS: Record<DueSection, string> = {
  today: 'notifications.sections.today',
  tomorrow: 'notifications.sections.tomorrow',
  thisWeek: 'notifications.sections.thisWeek',
  earlier: 'notifications.sections.earlier',
}

// A row of the list: a notification, or the green card of something just done.
type Row =
  | { kind: 'notification'; notification: FormattedNotification }
  | { kind: 'confirmation'; confirmation: Confirmation }

interface LaterCounts {
  keepContacts?: number
  calendarEvents?: number
  scheduledMessages?: number
}

export const Notifications: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)
  const navigation = useNavigation()

  const [activeTab, setActiveTab] = useState<NotificationTabKey>('all')

  // Fetch notifications from API
  const {
    flatData: apiNotifications = [],
    isFetchingNextPage: isFetchingNextPageNotifications,
    hasNextPage: hasNextPageNotifications,
    fetchNextPage: fetchNextPageNotifications,
    pageMetadata,
    refetch: refetchNotifications,
    isLoading,
    isPlaceholderData,
    isError,
    isFetching,
    isPaused,
  } = usePaginateNotifications(activeTab)
  // Offline, the web app does not fail the load: it waits (paused) until the
  // network is back. Nothing was loaded either way.
  const loadFailed = isError || isPaused

  const { mutate: answerContactRequest } = useAnswerRequestedContacts()
  const { mutate: markAsRead } = useMarkNotificationAsRead()
  const { mutate: markAllAsRead, isPending: isMarkingAll } = useMarkAllNotificationsAsRead()
  const { mutateAsync: markDone } = useKeepContactDone()
  const { mutate: postponeKeepContact } = usePostponeKeepContact()
  const { showNotificationSettings, isCreationStackEnabled } = useCreationStackIntegration()
  const { isMobile } = useScreen()

  // What was just done here (« C'est fait », a request accepted), shown in green.
  const [confirmations, setConfirmations] = useState<Confirmation[]>([])

  useFocusEffect(
    useCallback(() => {
      void refetchNotifications()
      // The green lines (« C'est fait » and its « Annuler ») belong to this
      // visit: an undo still offered after the person has gone elsewhere could
      // bring a follow-up back over the free plan's limit.
      return () => setConfirmations([])
    }, [refetchNotifications]),
  )

  // Filter and format notifications by type
  const formattedNotifications = useMemo((): FormattedNotification[] => {
    return apiNotifications.map((notification: ApiNotification): FormattedNotification => {
      const isRead = notification.readAt !== null

      // Format message based on notification type
      let message = ''
      let contactId: string | undefined
      let contactIds: string[] | undefined
      let contactRequestId: string | undefined
      let accepted: boolean | undefined
      let description: string | undefined

      if (notification.type === NotificationType.CONTACT_REQUEST_CREATED) {
        const data = notification.data as { contactRequestId: string; senderId: string }
        message = t('notifications.friendRequest')
        contactId = data.senderId
        contactRequestId = data.contactRequestId
      } else if (notification.type === NotificationType.CONTACT_REQUEST_ANSWERED) {
        const data = notification.data as {
          contactRequestId: string
          recipientId: string
          answer: ContactRequestAnswer
        }
        message =
          data.answer === ContactRequestAnswer.CONFIRMED
            ? t('notifications.acceptedRequest')
            : t('notifications.rejectedRequest')
        contactId = data.recipientId
        contactRequestId = data.contactRequestId
        accepted = data.answer === ContactRequestAnswer.CONFIRMED
      } else if (notification.type === NotificationType.CALENDAR_EVENT_UPCOMING) {
        const data = notification.data as {
          calendarEventId: string
          startDateTime: string
          participantAccountIds?: string[]
        }
        message = t('notifications.upcomingEvent')
        contactIds = data.participantAccountIds
        description = dayjs(data.startDateTime).format('DD/MM/YYYY HH:mm')
      } else if (notification.type === NotificationType.KEEP_CONTACT_REMINDER) {
        const data = notification.data as {
          keepContactId: string
          contactId: string
          keepDate: string
          pushCopy?: { title: string; body: string }
        }
        // Post-event follow-up ("Relancer Julien ?"): its own text, not the generic reminder.
        message = data.pushCopy?.title ?? t('notifications.contactReminder')
        contactId = data.contactId
        description = data.pushCopy?.body ?? dayjs(data.keepDate).format('DD/MM/YYYY HH:mm')
      } else if (notification.type === NotificationType.SCHEDULED_MESSAGE_UPCOMING) {
        const data = notification.data as {
          scheduledMessageId: string
          contactId: string
          scheduledAt: string
        }
        message = t('notifications.scheduledMessage')
        contactId = data.contactId
        description = dayjs(data.scheduledAt).format('DD/MM/YYYY HH:mm')
      } else if (notification.type === NotificationType.BIRTHDAY_UPCOMING) {
        const data = notification.data as { contactId: string; birthDate: string }
        message = t('notifications.birthday')
        contactId = data.contactId
        description = dayjs(birthDayOf(data.birthDate), 'YYYY-MM-DD').format('DD/MM/YYYY')
      } else if (notification.type === NotificationType.EVENT_REMINDER) {
        const data = notification.data as { pushCopy?: { title: string; body: string } }
        message = data.pushCopy?.title ?? t('notifications.eventReminder')
        description = data.pushCopy?.body
      } else if (notification.type === NotificationType.EVENT_ORGANIZER_MESSAGE) {
        const data = notification.data as { subject: string; pushCopy?: { title: string; body: string } }
        message = data.pushCopy?.title ?? data.subject
        description = data.pushCopy?.body
      }

      return {
        ...notification,
        message,
        contactId,
        contactIds,
        contactRequestId,
        accepted,
        isRead,
        description,
      }
    })
  }, [apiNotifications, t])

  // Aujourd'hui / Demain / Cette semaine / Plus tôt, by when each notification
  // is due; what was just done opens « Aujourd'hui ». A card just done or
  // accepted gives way to its green confirmation.
  const sections = useMemo(() => {
    const replaced = new Map(confirmations.map((confirmation) => [confirmation.id, confirmation]))
    const visible = formattedNotifications.filter((notification) => {
      const confirmation = replaced.get(notification.id)
      if (!confirmation) return true
      // A repeating follow-up stays once it moved to its next date.
      return (
        confirmation.kind === 'done' &&
        notification.type === NotificationType.KEEP_CONTACT_REMINDER &&
        !dayjs(notification.eventAt ?? undefined).isSame(dayjs(confirmation.previousDate))
      )
    })
    const grouped = groupByDueSection(visible)
    const toRows = (data: FormattedNotification[]): Row[] =>
      data.map((notification) => ({ kind: 'notification', notification }))
    const confirmationRows: Row[] =
      activeTab === 'all' ? confirmations.map((confirmation) => ({ kind: 'confirmation', confirmation })) : []
    const result = grouped.map((section) => ({
      key: section.key,
      title: t(SECTION_LABEL_KEYS[section.key]),
      data: section.key === 'today' ? [...confirmationRows, ...toRows(section.data)] : toRows(section.data),
    }))
    if (confirmationRows.length > 0 && !grouped.some((section) => section.key === 'today')) {
      result.unshift({ key: 'today', title: t(SECTION_LABEL_KEYS.today), data: confirmationRows })
    }
    return result
  }, [formattedNotifications, confirmations, activeTab, t])

  // Under the cards of « Demandes »: the requests I sent, and the received
  // ones no card offers to answer. Which received ones have a card is only
  // known once every page of this tab is loaded.
  const {
    sent: sentRequests,
    received: receivedRequests,
    refetch: refetchPendingRequests,
  } = usePendingContactRequests()
  // Read again with the notifications each time the screen comes back: a
  // request cancelled or answered meanwhile would otherwise stay listed.
  useFocusEffect(
    useCallback(() => {
      void refetchPendingRequests()
    }, [refetchPendingRequests]),
  )
  const isRequestsTab = activeTab === 'requests' && !isPlaceholderData
  const receivedRequestsWithoutCard = useMemo(() => {
    if (!isRequestsTab || hasNextPageNotifications) return []
    const cardRequestIds = new Set(
      formattedNotifications
        .filter((notification) => notification.type === NotificationType.CONTACT_REQUEST_CREATED)
        .map((notification) => notification.contactRequestId ?? ''),
    )
    return receivedWithoutCard(receivedRequests, cardRequestIds)
  }, [isRequestsTab, hasNextPageNotifications, formattedNotifications, receivedRequests])
  const hasPendingRequestsFooter =
    isRequestsTab && (sentRequests.length > 0 || receivedRequestsWithoutCard.length > 0)

  // NOTE: Filtering is done server-side via the `tab` query param.
  // This list already contains the correct notifications for the active tab.
  const allCount =
    pageMetadata?.countsByTab?.all?.unread ?? formattedNotifications.filter((n) => !n.isRead).length
  const requestsCount = pageMetadata?.countsByTab?.requests?.unread ?? 0
  const agendaCount = pageMetadata?.countsByTab?.agenda?.unread ?? 0

  const tabs = useMemo(
    () => [
      { key: 'all' as const, label: t('notifications.all'), count: allCount },
      { key: 'requests' as const, label: t('notifications.requests'), count: requestsCount },
      { key: 'agenda' as const, label: t('notifications.agenda'), count: agendaCount },
    ],
    [t, allCount, requestsCount, agendaCount],
  )

  // « Accepter en privé » or « Accepter en pro »: the card turns green.
  const handleAccept = (notification: FormattedNotification, scope: Scope) => {
    if (!notification.contactRequestId) return
    answerContactRequest(
      {
        requestedContactId: notification.contactRequestId,
        answer: 'CONFIRM',
        scope,
      },
      {
        onSuccess: () => {
          if (!notification.contactId) return
          setConfirmations((current) => [
            { kind: 'accepted', id: notification.id, personId: notification.contactId!, scope },
            ...current.filter((confirmation) => confirmation.id !== notification.id),
          ])
        },
      },
    )
  }

  // « C'est fait »: the card turns green, with the next reminder and « Annuler ».
  const handleDone = async (notification: FormattedNotification) => {
    const keepContactId = (notification.data as { keepContactId?: string }).keepContactId
    if (!keepContactId || !notification.contactId) return
    try {
      const result = await markDone(keepContactId)
      if (!result.previousDate) return
      setConfirmations((current) => [
        {
          kind: 'done',
          id: notification.id,
          personId: notification.contactId!,
          previousDate: result.previousDate!,
          nextDate: result.nextDate,
        },
        ...current.filter((confirmation) => confirmation.id !== notification.id),
      ])
    } catch (error) {
      showApiErrorToast(t, error)
    }
  }

  // « Annuler » after « C'est fait »: the reminder comes back as it was.
  const handleUndo = (confirmation: Confirmation) => {
    if (confirmation.kind !== 'done') return
    postponeKeepContact(
      { id: confirmation.id, date: confirmation.previousDate },
      {
        onSuccess: () =>
          setConfirmations((current) => current.filter((other) => other.id !== confirmation.id)),
        onError: (error) => showApiErrorToast(t, error),
      },
    )
  }

  const handlePostpone = (notification: FormattedNotification, date: string) => {
    const keepContactId = (notification.data as { keepContactId?: string }).keepContactId
    if (!keepContactId) return
    postponeKeepContact(
      { id: keepContactId, date },
      { onError: (error) => showApiErrorToast(t, error) },
    )
  }

  const retry = () => void refetchNotifications()

  // An older list stays on screen when refreshing it fails: say so, discreetly.
  const renderStale = () => (
    <View style={styles.stale}>
      <Text type="bodySmall" color={theme.colors.greyBlue}>
        {t('notifications.loadError.stale')}
      </Text>
      <TouchableOpacity
        testID="notifications-retry-stale"
        accessibilityRole="button"
        disabled={isFetching}
        onPress={retry}
      >
        <Text type="bodySmallBold" color={theme.colors.primary}>
          {t('notifications.loadError.retry')}
        </Text>
      </TouchableOpacity>
    </View>
  )

  const openSettings = () => {
    if (isCreationStackEnabled) showNotificationSettings()
    else (navigation as any).navigate('Menu', { screen: 'NotificationSettings' })
  }

  const handleReject = (contactRequestId: string) => {
    answerContactRequest({
      requestedContactId: contactRequestId,
      answer: 'DECLINE',
    })
  }

  const handleRead = (notification: FormattedNotification) => {
    if (!notification.isRead) {
      markAsRead(notification.id)
    }
  }

  const renderRow = ({ item }: { item: Row }) =>
    item.kind === 'confirmation' ? (
      <NotificationConfirmation
        confirmation={item.confirmation}
        onUndo={item.confirmation.kind === 'done' ? () => handleUndo(item.confirmation) : undefined}
      />
    ) : (
      <NotificationItem
        notification={item.notification}
        onPress={() => handleRead(item.notification)}
        onAccept={(scope) => handleAccept(item.notification, scope)}
        onReject={() => item.notification.contactRequestId && handleReject(item.notification.contactRequestId)}
        onDone={() => void handleDone(item.notification)}
        onPostpone={(date) => handlePostpone(item.notification, date)}
      />
    )

  // « Prévu plus tard : 3 suivis, 1 rendez-vous. » — they show up here when their time comes.
  const later = (pageMetadata?.later ?? {}) as LaterCounts
  const renderLater = () => {
    if (activeTab === 'requests') return null
    const parts = [
      later.keepContacts ? t('notifications.later.keepContacts', { count: later.keepContacts }) : null,
      later.calendarEvents ? t('notifications.later.calendarEvents', { count: later.calendarEvents }) : null,
      later.scheduledMessages
        ? t('notifications.later.scheduledMessages', { count: later.scheduledMessages })
        : null,
    ].filter((part): part is string => Boolean(part))
    if (parts.length === 0) return null
    const go = (screen: string) => (navigation as any).navigate('Menu', { screen })
    return (
      <View style={styles.later}>
        <Text type="bodySmall" color={theme.colors.greyBlue}>
          {t('notifications.later.line', { items: parts.join(', ') })}
        </Text>
        <View style={styles.laterLinks}>
          {later.keepContacts ? (
            <TouchableOpacity onPress={() => go('KeepContact')} accessibilityRole="link">
              <Text type="bodySmallBold" color={theme.colors.primary}>
                {t('notifications.later.seeKeepContacts')}
              </Text>
            </TouchableOpacity>
          ) : null}
          {later.calendarEvents ? (
            <TouchableOpacity onPress={() => go('Calendar')} accessibilityRole="link">
              <Text type="bodySmallBold" color={theme.colors.primary}>
                {t('notifications.later.seeCalendar')}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    )
  }

  return (
    <Screen>
      <Header
        title={t('tabNavigation.notifications')}
        right={
          <View style={styles.headerActions}>
            <TouchableOpacity
              testID="notifications-mark-all-read"
              accessibilityRole="button"
              disabled={allCount === 0 || isMarkingAll}
              onPress={() => markAllAsRead()}
              style={[styles.markAllRead, allCount === 0 && styles.markAllReadDisabled]}
            >
              <Icon name="check" size={theme.iconSize.small} color={theme.colors.primary} />
              <Text type="bodySmallBold" color={theme.colors.primary}>
                {isMobile ? t('notifications.markAllReadShort') : t('notifications.markAllRead')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="notifications-settings"
              accessibilityRole="button"
              accessibilityLabel={t('notificationSettings.open')}
              onPress={openSettings}
              style={styles.settingsButton}
            >
              <Icon name="settings" size={theme.iconSize.medium} color={theme.colors.greyBlue} />
            </TouchableOpacity>
          </View>
        }
      />

      <View style={styles.body}>
        <NotificationTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />

        {isLoading ? (
          <NotificationSkeleton />
        ) : (
          // Dim slightly while a tab switch / push refetch loads the next list,
          // so the previous content stays put instead of flashing blank.
          <View style={[styles.listWrapper, isPlaceholderData && styles.listLoading]}>
            <SectionList
              sections={sections}
              renderItem={renderRow}
              keyExtractor={(item) =>
                item.kind === 'confirmation' ? `confirmation-${item.confirmation.id}` : item.notification.id
              }
              renderSectionHeader={({ section }) => (
                <Text
                  type="bodySmallBold"
                  color={theme.colors.greyBlue}
                  style={styles.sectionHeader}
                >
                  {section.title}
                </Text>
              )}
              stickySectionHeadersEnabled={false}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.listContent}
              ListHeaderComponent={loadFailed && apiNotifications.length > 0 ? renderStale() : null}
              // « Tout est à jour » would be untrue after a failed load, or
              // above requests still waiting.
              ListEmptyComponent={
                loadFailed ? (
                  <NotificationsLoadError onRetry={retry} retrying={isFetching} />
                ) : hasPendingRequestsFooter ? null : (
                  <NotificationsEmptyState />
                )
              }
              onEndReached={() => {
                if (hasNextPageNotifications && !isFetchingNextPageNotifications) {
                  void fetchNextPageNotifications()
                }
              }}
              onEndReachedThreshold={0.5}
              ListFooterComponent={
                isFetchingNextPageNotifications ? (
                  <ActivityIndicator color={theme.colors.primary} style={styles.footerLoader} />
                ) : hasPendingRequestsFooter ? (
                  <PendingRequestsFooter sent={sentRequests} received={receivedRequestsWithoutCard} />
                ) : (
                  renderLater()
                )
              }
            />
          </View>
        )}
      </View>
    </Screen>
  )
}
